import React, { useContext, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { ThemeContext } from '../../context/ThemeContext';
import { NetworkContext } from '../../context/NetworkContext';
import { Copy } from 'lucide-react';

import 'react-datepicker/dist/react-datepicker.css';

import CustomButton from '../Componentization/CustomButton';
import CustomText from '../../styles/CustomText';

import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { checkHeaderText } from '../../store';

import i18next from 'i18next';
import useSecureStorage from '../../hooks/useSecureStorage';

import BigNumber from 'bignumber.js';

interface Wallet {
    id: string;
    type: string;
    amount: string;
    currency: string;
    date: any;
    detaildate: any;
    direction: 'in' | 'out';
    from: string;
    to: string;
    fee: string;

    // ✅ 내부이체 메타데이터 (WalletScreen에서 넘어온 값)
    isInternal?: boolean;
    qPin?: number;
    shopName?: string;
}

interface Props {
    item: string;
}

type RootStackParamList = {
    WalletScreenDetail: { item: Wallet };
};

function shortenString(str: string): string {
    if (str == null) {
        return ``;
    }

    if (str.length <= 10) {
        return str;
    }

    const start = str.slice(0, 6);
    const end = str.slice(-4);

    return `${start}...${end}`;
}

const WalletScreenDetail: React.FC = () => {
    const { theme } = useContext(ThemeContext);
    let navigate = useNavigate();
    let dispatch = useDispatch();
    const { t } = useTranslation();

    const location = useLocation();
    const [item, setItem] = useState<Wallet>(
        location.state === null ? (null as any) : location.state.item,
    );
    const [gasFee, setGasFee] = useState('0');

    const { getBlockChain } = useSecureStorage();
    const { getBlockScanUrl } = useContext(NetworkContext);

    useEffect(() => {
        dispatch(
            checkHeaderText({
                link: 'WalletScreenDetail',
                title: t('wallet_detail_screen_message_01'),
            }),
        );
        console.log(`WalletScreenDetail item : `, item);
    }, []);

    const copyToClipboard = async (value: string) => {
        if (value) {
            navigator.clipboard
                .writeText(value)
                .then(() => {
                    alert(i18next.t('template_receive_screen_message_01'));
                })
                .catch((err) => {
                    console.error('클립보드 복사 실패:', err);
                });
        }
    };

    const handleOpenExplorer = async () => {
        const selNetwork = await getBlockChain();
        const fullURL = await getBlockScanUrl(item.id.split('-')[0]);
        console.log(`fullURL : `, fullURL);

        await window.open(fullURL, '_blank');
    };

    const syncFormatNumberWithCommas = (
        input: BigNumber,
        decimalPlaces: number = 9,
    ): string => {
        if (input === null || input.isNaN()) {
            return '0.' + '0'.repeat(decimalPlaces);
        }

        const absInput = input.abs();

        const scale = new BigNumber(10).pow(decimalPlaces);
        const floored = absInput
            .multipliedBy(scale)
            .integerValue(BigNumber.ROUND_FLOOR)
            .dividedBy(scale);
        const fixedStr = floored.toFixed(decimalPlaces);

        let [intPart, decPart] = fixedStr.split('.');
        intPart = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');

        if (decPart) {
            decPart = decPart.replace(/0+$/, '');
            if (decPart.length === 0) {
                return intPart + '.00';
            }
            return intPart + '.' + decPart;
        }
        return intPart + '.00';
    };

    // 폰트 크기 조절
    const getDynamicFontSize = (number: string): number => {
        const length = number.length;
        if (length <= 20) return 20;
        if (length <= 25) return 18;
        if (length <= 30) return 16;
        if (length <= 35) return 14;
        if (length <= 40) return 12;
        return 12;
    };

    // ✅ seller 메타데이터 존재 여부
    const hasSellerMeta = item && (item.qPin != null || !!item.shopName);
    const sellerLabel = hasSellerMeta
        ? `${item.qPin != null ? `[${item.qPin}]` : ''}${item.shopName ?? ''}`
        : '';

    const styles = {
        container: {
            padding: '60px 0px 24px',
        } as React.CSSProperties,
        top: {
            padding: '20px 30px',
            width: '100%',
            backgroundColor: theme.background_10,
            alignItems: 'center',
            borderBottomRightRadius: 10,
            borderBottomLeftRadius: 10,
            boxSizing: 'border-box',
            display: 'flex',
            flexDirection: 'column',
            alignSelf: 'center',
            justifyContent: 'center',
        } as React.CSSProperties,
        topIcon: {
            width: 30,
            height: 30,
            marginBottom: 10,
        } as React.CSSProperties,
        transactionAmountContainer: {
            display: 'flex',
            height: 30,
            gap: 10,
            alignItems: 'center',
        } as React.CSSProperties,
        transactionAmount: {
            fontSize: 20,
        } as React.CSSProperties,
        transactionSymbol: {
            color: theme.text_01,
            fontSize: 18,
        } as React.CSSProperties,
        fee: {
            display: 'flex',
            width: '100%',
            justifyContent: 'space-between',
            alignItems: 'center',
        } as React.CSSProperties,
        fee1: {
            width: '100%',
            textAlign: 'right',
        } as React.CSSProperties,
        feeAmount: {
            fontSize: 20,
        } as React.CSSProperties,
        // ✅ 내부 결제(매장) 정보 스타일
        sellerBox: {
            marginTop: 10,
            paddingTop: 6,
            borderTop: `1px solid ${theme.border_03}`,
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 4,
        } as React.CSSProperties,
        sellerLabel: {
            fontSize: 12,
            color: theme.text_03,
        } as React.CSSProperties,
        sellerValue: {
            fontSize: 18,
            color: theme.text_01,
            fontWeight: 'bold',
        } as React.CSSProperties,
        top2: {
            width: '100%',
            paddingTop: 20,
        } as React.CSSProperties,
        top2Box: {
            padding: '0 30px',
            width: '100%',
            fontSize: 16,
            height: 35,
            marginBottom: 5,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            boxSizing: 'border-box',
        } as React.CSSProperties,
        top3Box: {
            padding: '0 20px',
            display: 'flex',
            width: '100%',
            justifyContent: 'space-between',
            fontSize: 16,
            height: 40,
            alignItems: 'center',
            boxSizing: 'border-box',
        } as React.CSSProperties,
        bottom: {
            paddingTop: 10,
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
        } as React.CSSProperties,
        explorerButton: {
            display: 'flex',
            alignItems: 'center',
            marginBottom: 20,
            border: `1px solid ${theme.border_03}`,
            borderRadius: 30,
            padding: '10px 15px',
            width: '50%',
            justifyContent: 'center',
            marginTop: 20,
            background: 'inherit',
        } as React.CSSProperties,
        explorerText: {
            color: theme.text_08,
            fontSize: 16,
        } as React.CSSProperties,
    };

    return (
        <div
            className="container"
            style={{ ...styles.container, backgroundColor: theme.background_normal }}
        >
            <div style={{ width: '100%' }}>
                <div style={styles.top}>
                    <img
                        src={item.direction === 'out' ? './img/out_icon.png' : './img/in_icon.png'}
                        alt="transaction icon"
                        style={styles.topIcon}
                    />
                    <CustomText
                        style={{
                            color: theme.text_01,
                            fontSize: 24,
                            marginBottom: 15,
                            fontWeight: 'bold',
                        }}
                    >
                        {item.direction === 'out'
                            ? `${i18next.t('wallet_detail_screen_message_02')}`
                            : `${i18next.t('wallet_detail_screen_message_03')}`}
                    </CustomText>
                    <div style={styles.transactionAmountContainer}>
                        <CustomText
                            weight="bold"
                            style={[
                                styles.transactionAmount,
                                {
                                    color:
                                        Number(item.amount) !== 0
                                            ? item.direction === 'out'
                                                ? theme.text_07
                                                : theme.text_18
                                            : theme.text_14,
                                },
                                {
                                    fontSize: getDynamicFontSize(
                                        syncFormatNumberWithCommas(
                                            new BigNumber(item.amount.toString()),
                                            9,
                                        ),
                                    ),
                                },
                            ] as any}
                        >
                            {Number(item.amount) !== 0 &&
                                (item.direction === 'out' ? '-' : '+')}
                            {syncFormatNumberWithCommas(
                                new BigNumber(item.amount.toString()),
                            )}
                        </CustomText>
                        <CustomText style={styles.transactionSymbol}>
                            {item.currency.toUpperCase()}
                        </CustomText>
                    </div>

                    <div style={styles.fee}>
                        <CustomText style={styles.transactionSymbol}>
                            {i18next.t('wallet_detail_screen_message_04')}
                        </CustomText>
                        <div style={styles.transactionAmountContainer}>
                            <CustomText
                                weight="bold"
                                style={[
                                    styles.feeAmount,
                                    {
                                        color:
                                            item.direction === 'out'
                                                ? theme.text_07
                                                : theme.text_14,
                                    },
                                    {
                                        fontSize: getDynamicFontSize(
                                            syncFormatNumberWithCommas(
                                                new BigNumber(item.fee.toString()),
                                                9,
                                            ),
                                        ),
                                    },
                                ] as any}
                            >
                                {item.direction === 'out'
                                    ? '-' +
                                    syncFormatNumberWithCommas(
                                        new BigNumber(item.fee.toString()),
                                    )
                                    : '0'}
                            </CustomText>
                            <CustomText style={styles.transactionSymbol}>
                                {process.env.NETWORK_CURRENCY}
                            </CustomText>
                        </div>
                    </div>
                </div>

                <div style={styles.top2}>
                    <div style={styles.top2Box}>
                        <CustomText style={{ color: theme.text_01, fontSize: 16 }}>
                            {i18next.t('wallet_detail_screen_message_05')}
                        </CustomText>
                        <CustomText style={{ color: theme.text_18, fontSize: 16 }}>
                            {i18next.t('wallet_detail_screen_message_06')}
                        </CustomText>
                    </div>
                    <div style={styles.top2Box}>
                        <CustomText style={{ color: theme.text_01, fontSize: 16 }}>
                            {i18next.t('wallet_detail_screen_message_07')}
                        </CustomText>
                        <CustomText style={{ color: theme.text_01, fontSize: 16 }}>
                            {item.detaildate}
                        </CustomText>
                    </div>

                    <div
                        style={{
                            backgroundColor: theme.background_10,
                            borderRadius: 10,
                            padding: '10px 0',
                            margin: '0 10px',
                        }}
                    >
                        {hasSellerMeta && (
                            <div style={styles.top3Box}>
                                <CustomText style={{ color: theme.text_01, fontSize: 16 }}>
                                    상점정보
                                </CustomText>
                                <div style={styles.transactionAmountContainer}>
                                    <CustomText
                                        style={{ color: theme.text_01, fontSize: 16 }}
                                    >
                                        {sellerLabel}
                                    </CustomText>
                                </div>
                            </div>
                        )}

                        <div style={styles.top3Box}>
                            <CustomText style={{ color: theme.text_01, fontSize: 16 }}>
                                {i18next.t('wallet_detail_screen_message_08')}
                            </CustomText>
                            <div style={styles.transactionAmountContainer}>
                                <CustomText
                                    style={{ color: theme.text_01, fontSize: 16 }}
                                >
                                    {shortenString(item.from)}
                                </CustomText>
                                <button onClick={() => copyToClipboard(item.from)}>
                                    <Copy
                                        color={theme.text_01}
                                        size={15}
                                        style={{ marginTop: 4 }}
                                    />
                                </button>
                            </div>
                        </div>

                        <div style={styles.top3Box}>
                            <CustomText style={{ color: theme.text_01, fontSize: 16 }}>
                                {i18next.t('wallet_detail_screen_message_09')}
                            </CustomText>
                            <div style={styles.transactionAmountContainer}>
                                <CustomText
                                    style={{ color: theme.text_01, fontSize: 16 }}
                                >
                                    {shortenString(item.to)}
                                </CustomText>
                                <button onClick={() => copyToClipboard(item.to)}>
                                    <Copy
                                        color={theme.text_01}
                                        size={15}
                                        style={{ marginTop: 4 }}
                                    />
                                </button>
                            </div>
                        </div>

                        <div style={styles.top3Box}>
                            <CustomText style={{ color: theme.text_01, fontSize: 16 }}>
                                {i18next.t('wallet_detail_screen_message_10')}
                            </CustomText>
                            <div style={styles.transactionAmountContainer}>
                                <CustomText
                                    style={{ color: theme.text_01, fontSize: 16 }}
                                >
                                    {shortenString(item.id.split('-')[0])}
                                </CustomText>
                                <button
                                    onClick={() => copyToClipboard(item.id.split('-')[0])}
                                >
                                    <Copy
                                        color={theme.text_01}
                                        size={15}
                                        style={{ marginTop: 4 }}
                                    />
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                <div style={styles.bottom}>
                    <CustomButton
                        containerStyle={{
                            ...styles.explorerButton,
                            borderColor: theme.primary_normal,
                        }}
                        textStyle={{
                            ...styles.explorerText,
                            color: theme.primary_normal,
                        }}
                        onPress={async () => await handleOpenExplorer()}
                        iconSize={20}
                        text={i18next.t('withdraw_complete_screen_message_07')}
                        iconName="open-outline"
                        withBackground={true}
                        backgroundColor={theme.background_09}
                    />
                </div>
            </div>
        </div>
    );
};

export default WalletScreenDetail;
